"use client";

/**
 * Listings search: one panel, two surfaces.
 *
 * The bar itself is a minimal pill. Focusing it opens the SAME
 * experience everywhere - full-screen sheet on phones, an anchored
 * card on desktop - with three layers of content:
 *
 *   1. Applied filters as removable chips (the way back: everything
 *      you've entered is visible here and can be edited out).
 *   2. While typing: instant suggestions - places, residences,
 *      developers, amenities, plus structured tokens (rooms/price/area) parsed
 *      deterministically, no LLM.
 *   3. While idle: quick picks (popular places + amenities) so the
 *      panel is a starting point, not an empty box.
 *
 * The paid LLM parse runs ONLY on Enter over free text the
 * suggestions could not cover.
 *
 * Deliberately styled as an ordinary search field (DESIGN.md: no AI
 * theatrics; the quiet UI keeps indigo for the main action only: the
 * "AI" label, the criteria chips and the tips are grey).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useListingAdvice } from "@/components/listings/ListingsAdvice";
import type { TranslationKey } from "@/lib/i18n";
import { useAffordability } from "@/lib/affordability";
import { createPortal } from "react-dom";
import { captureEvent } from "@/lib/tracking";
import { Camera } from "lucide-react";
import Link from "@/lib/localized-link";
import { searchByImage, searchByImageUrl } from "@/lib/api";
import { useFeatureFlag } from "@/lib/features";
import { listingPath } from "@/lib/seo/paths";
import type { ImageSearchResponse, ResidenceMatch } from "@/types";
import { useBackToClose } from "@/lib/use-back-to-close";
import { trackEvent } from "@/lib/tracking";
import { useSearchParams } from "next/navigation";
import { ArrowLeft, Banknote, BedDouble, Building, Building2, CalendarDays, Footprints, Info, Lightbulb, Loader2, MapPin, MapPinned, Ruler, Search, Sparkles, Tag, TextSearch, TriangleAlert, UserRound, X , Route } from "lucide-react";
import { useT, useLocale } from "@/lib/locale-context";
import {
  magicSearch, getSearchSuggestions, fetchAPI, lookupListingLink, formatPrice, currencySign,
  type SearchSuggestion,
} from "@/lib/api";
import { isEverythingQuery, plainParams } from "@/lib/magic-plain";
import { cachedFilterNames, loadFilterNames, type FilterNames } from "@/lib/filter-names";
import { LISTING_KINDS, isSeparate, kindChipKey, kindNounKey } from "@/lib/property-kinds";
import { evaluationHref, listingLinkOf, looksLikeLink, looksLikeImageLink, sourceHostLabel } from "@/lib/listing-url";
import ImageSearchDialog, { pickPhoto, type PhotoNote } from "@/components/listings/ImageDropZone";
import { useIsMobileViewport } from "@/lib/use-install-prompt";
import { useLocalizedRouter } from "@/lib/localized-router";
import DeveloperLogo from "@/components/developers/DeveloperLogo";
import Photo from "@/components/Photo";
import type { ListingCard } from "@/types";

const KIND_ICON: Record<SearchSuggestion["kind"], typeof Search> = {
  location: MapPin,
  city: MapPinned,
  residence: Building2,
  developer: Building,
  street: Route,
  tag: Tag,
  rooms: BedDouble,
  price: Banknote,
  area: Ruler,
  seller: UserRound,
  metro: Footprints,
  rent_period: CalendarDays,
};

/** URL keys the search surface knows how to display and remove. */
// Chips in the bar cover ONLY what the filter row below cannot show
// (owner, 2026-09-20: "why duplicate the value in the search bar and in
// the filter inputs"). Rooms, price, deal type, kind and location have
// their own controls there; tags and size do not.
// The developer lives inside "more filters", so it shows here too.
// `street` = street ids (backend ?street=, repeatable; services/streets):
// each chip carries the street's name and place, fetched once per id.
// `city` = towns outside Baku by name (backend ?city=, repeatable;
// services/cities): the filter row has no control for them.
// `exclude_*`: what the sentence ruled out ("Xırdalan olmasın").
const MULTI_KEYS = ["tags", "developer", "street", "city", "exclude_location_ids", "exclude_city", "exclude_tags"] as const;
// Suggestion rows read straight from the typed text (numbers, units,
// seller words): always what was typed, so Enter may take them.
const LITERAL_KINDS = new Set(["rooms", "price", "area", "seller", "metro", "rent_period"]);
// `q` = keyword search over the description text (backend ?q=).
const SCALAR_KEYS = ["q", "area_min", "area_max", "rent_period", "metro_walk"] as const;
// The phone sheet covers the filter row, so it lists EVERYTHING applied
// (otherwise a picked location seems to vanish - owner, 2026-09-20).
const SHEET_MULTI_KEYS = ["location_ids", "complex_slug", "rooms", "listing_kind", "property_kind"] as const;
const SHEET_SCALAR_KEYS = ["leased", "category", "building_type", "price_min", "price_max"] as const;
/** The kind picker's word for a category value ("Torpaq", "Qaraj"). */
function categoryKey(v: string): TranslationKey | null {
  return (LISTING_KINDS as readonly string[]).includes(v) ? (`listings.filter.category.${v}` as TranslationKey) : null;
}
/** Idle-panel amenities, in display order: the ones that make a buyer
 *  lean in, not the ones every listing has. */
const WOW_TAGS = [
  "sea_view", "beach", "panoramic", "pool", "terrace", "penthouse", "smart_home",
  "designer_repair", "sauna", "fireplace", "park_view", "closed_yard", "spa",
];
/** Which filter-row chip a picked suggestion lands in (for the pulse). */
const PULSE_TARGET: Partial<Record<SearchSuggestion["kind"], string>> = {
  location: "location", residence: "location", street: "location", rooms: "rooms", price: "price",
};
// Seller type has no chip in the filter row; it shows in the bar instead.

/** What the index said about a link pasted into the bar. */
type LinkAnswer =
  | { url: string; kind: "found"; card: ListingCard }
  | { url: string; kind: "missing"; why: "absent" | "inactive" | "error" };
/** The bar's one row for a pasted link: the answer, or the lookup in flight. */
type LinkRow = LinkAnswer | { url: string; kind: "loading" };

type AppliedChip = {
  id: string;
  label: string;
  /** Leading glyph instead of a word (the text-search chip: owner,
   *  2026-09-20, "I want to see it as an icon"); `title` explains it on
   *  hover. */
  icon?: "text";
  title?: string;
  /** Exact URL updates that undo this chip (null clears a key). */
  remove: Record<string, string[] | null>;
  /** Merge payload that re-applies this chip (Ctrl+Z after an
   *  empty-input Backspace removal). */
  restore: Record<string, string | string[]>;
};

function fmtNum(v: string): string {
  return v.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/* Reference data for chip labels + quick picks - tiny, static per
   deploy, cached at module level so reopening the panel is free. */
const loadNames = loadFilterNames;

/** A whole-box link a home bar sends to the valuation (lib/listing-url
 *  evaluationHref); a link to a photo stays a photo search. */
function isEvaluable(text: string): boolean {
  return evaluationHref(text) != null && !looksLikeImageLink(text.trim());
}

/** All picked room counts as ONE short chip, in order ("2, 5+ otaqlı"):
 *  a chip per value read "5+ otaqlı, 2 otaqlı" in pick order (owner,
 *  2026-10-02). */
function roomsChip(values: string[], t: (k: TranslationKey) => string): AppliedChip | null {
  const sorted = [...new Set(values)].filter(Boolean).sort((a, b) => Number(a) - Number(b));
  if (!sorted.length) return null;
  const n = sorted.map((v) => (Number(v) >= 5 ? "5+" : v)).filter((v, i, a) => a.indexOf(v) === i).join(", ");
  return { id: "rooms", label: t("listings.magic.chip.rooms").replace("{n}", n), remove: { rooms: null }, restore: { rooms: sorted } };
}

function MagicSearchBarBody({
  urlParams,
  searchParams: spOverride,
  aiEnabled = true,
  onApplyAll,
  onMergeOne,
  onReplaceOne,
  onClearAll,
  compact = false,
  chipsOutside = false,
  onLink,
  onOpenListing,
  onEvaluateLink,
  onQueryChange,
}: {
  /** A pasted link, before any parse: return true when it was handled
   *  (the hero opens a listing or the analyzer); false lets it through
   *  as text. */
  onLink?: (url: string) => boolean;
  /** The home bars: any link, typed or pasted, is a valuation (owner,
   *  2026-10-29: "When we present a link, it should go and try to
   *  evaluate it"). The link gets ONE row that sends it here, Enter and
   *  the phone's Go run it, and it is never looked up or suggested on.
   *  Takes precedence over onOpenListing and onLink. A link to a photo
   *  stays a photo search. */
  onEvaluateLink?: (url: string) => void;
  /** The text in the box, as it changes: the host's own button follows
   *  it (the home hub's "Qiymətləndirin" for a link). */
  onQueryChange?: (q: string) => void;
  /** Turns link recognition on. A link to a listing on one of the sites
   *  we read, pasted or typed, is looked up in the index: an active
   *  listing becomes the bar's one row and opens through this callback,
   *  the way the page opens a listing (the drawer on /listings, the pin
   *  on /map, its page on the hub); any other listing gets the analysis
   *  offer (/analyze). Without it a link is words, as before (the alert
   *  form). Owner, 2026-10-17: "if I paste the link into the search bar,
   *  I want it to be identified". */
  onOpenListing?: (card: ListingCard) => void;
  /** One line inside another toolbar (the map page): no bottom margin,
   *  fills its flex slot, the panel keeps a readable width. */
  compact?: boolean;
  /** The page shows the applied filters as its own chip row (the
   *  listings page, 2026-10-29): the bar keeps only the text, so no
   *  filter shows twice. */
  chipsOutside?: boolean;
  /** Admin flag `magic_search`. Off = no LLM parse and no AI mark; the
   *  bar still suggests entities and Enter searches the description
   *  text by keyword (?q=). */
  aiEnabled?: boolean;
  /** Replace committed filters with a full sentence parse. */
  onApplyAll: (params: Record<string, string | string[]>) => void;
  /** Merge one picked entity into the committed filters. */
  onMergeOne: (params: Record<string, string | string[]>) => void;
  /** Set a key to exactly these values (null clears it) - powers
   *  removing an applied chip from the panel. */
  onReplaceOne: (params: Record<string, string | string[] | null>) => void;
  /** Clear every committed filter (the bar's own × at its right end). */
  onClearAll?: () => void;
  /** The effective filter state when it is not only the URL's (a landing
   *  page pre-applies its own filters). Defaults to the URL. */
  searchParams?: URLSearchParams;
  urlParams: URLSearchParams | ReturnType<typeof useSearchParams>;
}) {
  const t = useT();
  const { locale } = useLocale();
  const searchParams = spOverride ?? urlParams;
  // Suggested fixes for this search (services/filter_advice), shown as
  // their own rows at the top of the panel.
  const advice = useListingAdvice(searchParams);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  // ── Search by photo (2026-10-18, feedback ticket 33) ────────────
  // A camera button, a pasted image or a dropped file: the photo goes
  // to /api/search/image, and the panel shows the residence it most
  // likely is (or three candidates) plus listings that look like it.
  // The camera opens a drop zone (owner, 2026-10-20), not the picker.
  const [dropOpen, setDropOpen] = useState(false);
  // Photo search sits behind its own switch (admin features, off by
  // default while the photo index fills): off hides the camera and leaves
  // pasted and dropped files to the browser.
  const photoSearch = useFeatureFlag("image_search", false);
  const mobileVp = useIsMobileViewport();
  const [imageNote, setImageNote] = useState<PhotoNote | null>(null);
  // The photo being searched, shown above the answer: an object URL for a
  // file (revoked when replaced), the link itself for a pasted link.
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const previewUrl = useRef<string | null>(null);
  const showPreview = useCallback((src: string | null, objectUrl: boolean) => {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = src && objectUrl ? src : null;
    setImagePreview(src);
  }, []);
  useEffect(() => () => { if (previewUrl.current) URL.revokeObjectURL(previewUrl.current); }, []);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageResult, setImageResult] = useState<ImageSearchResponse | null>(null);
  const [items, setItems] = useState<SearchSuggestion[]>([]);
  // The text the shown suggestions were fetched for: a quick Enter can
  // come before the debounced fetch lands (usage review, 2026-10-20).
  const itemsFor = useRef("");
  const qNow = useRef("");
  qNow.current = q;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [sheet, setSheet] = useState(false);
  const [names, setNames] = useState<FilterNames | null>(() => cachedFilterNames());
  const [mounted, setMounted] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Compact (the map toolbar): the bar keeps one row and floats open over
  // the map while in use, so the toolbar never grows and the layer switch
  // below it stays visible (owner, 2026-10-12).
  const [focusIn, setFocusIn] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── A pasted listing link (link recognition) ───────────────────
  // The callback lives in a ref: pages pass an inline function, and its
  // identity must not re-run the lookup effect below.
  const linkOn = !!onOpenListing && !onEvaluateLink;
  const openListingRef = useRef(onOpenListing);
  openListingRef.current = onOpenListing;
  const evaluateRef = useRef(onEvaluateLink);
  evaluateRef.current = onEvaluateLink;
  const queryChangeRef = useRef(onQueryChange);
  queryChangeRef.current = onQueryChange;
  useEffect(() => { queryChangeRef.current?.(q); }, [q]);
  const router = useLocalizedRouter();
  const link = linkOn ? listingLinkOf(q) : null;
  const [linkAnswer, setLinkAnswer] = useState<LinkAnswer | null>(null);
  // The link the latest lookup is for: an answer for an older one is dropped.
  const wantedLink = useRef<string | null>(null);
  // Enter pressed while the lookup was out: the row runs once it lands.
  const linkEnterPending = useRef(false);
  const linkRow: LinkRow | null = link
    ? (linkAnswer?.url === link ? linkAnswer : { url: link, kind: "loading" })
    : null;

  useEffect(() => setMounted(true), []);

  // The page-level Reset (the red button in the grid toolbar, the panel
  // footer, the phone sheet) clears the URL, but the typed text lives
  // here. Listen for its event and empty the input with it.
  useEffect(() => {
    const onReset = () => {
      setQ("");
      setItems([]);
      setHint(null);
      setActive(-1);
      setOpen(false);
    };
    window.addEventListener("menzil:filters-reset", onReset);
    return () => window.removeEventListener("menzil:filters-reset", onReset);
  }, []);

  // Reference names load once - on first open of either surface, or
  // straight away when the URL already carries params whose in-bar
  // chips need labels (tag/location/residence names).
  useEffect(() => {
    const needsNames = ["tags", "location_ids", "complex_slug", "developer", "exclude_location_ids", "exclude_tags"].some(
      (k) => searchParams.getAll(k).length > 0,
    );
    if ((open || sheet || needsNames) && !names) {
      loadNames().then(setNames).catch(() => {});
    }
  }, [open, sheet, names, searchParams]);

  // Debounced suggestion fetch; stale responses are dropped by length
  // check on the current query.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    // Any edit cancels an Enter that was waiting for a lookup.
    linkEnterPending.current = false;
    if (evaluateRef.current && isEvaluable(query)) {
      // A link to evaluate is one row; nothing to suggest or look up.
      wantedLink.current = null;
      setItems([]);
      setActive(-1);
      return;
    }
    const url = linkOn ? listingLinkOf(query) : null;
    wantedLink.current = url;
    if (url) {
      // A listing link is looked up in the index instead of suggested
      // on: no place or amenity is ever named like one.
      setItems([]);
      setActive(-1);
      debounceRef.current = setTimeout(() => {
        lookupListingLink(url)
          .then((r) => {
            if (wantedLink.current !== url) return;
            setLinkAnswer(r.card
              ? { url, kind: "found", card: r.card }
              : { url, kind: "missing", why: r.id && r.status !== "active" ? "inactive" : "absent" });
          })
          .catch(() => {
            if (wantedLink.current === url) setLinkAnswer({ url, kind: "missing", why: "error" });
          });
      }, 150);
      return;
    }
    if (query.length < 2) {
      setItems([]);
      setActive(-1);
      return;
    }
    debounceRef.current = setTimeout(() => {
      getSearchSuggestions(query)
        .then((s) => {
          setItems(s);
          itemsFor.current = query;
          setActive(-1);
        })
        .catch(() => {});
    }, 150);
  }, [q, linkOn]);

  // Click-away closes the desktop panel.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // The sheet owns the screen: no body scroll behind it.
  useEffect(() => {
    if (!sheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [sheet]);

  // Sheet motion: ``sheet`` mounts the portal, ``sheetOn`` drives the
  // visible classes. Opening plays the search-sheet-in keyframes on
  // mount (globals.css); closing flips the classes and unmounts after
  // the 180 ms transition - so it slides down instead of vanishing.
  const [sheetOn, setSheetOn] = useState(false);
  const openSheet = useCallback(() => {
    setSheet(true);
    setSheetOn(true);
  }, []);
  const closeSheet = useCallback(() => {
    setSheetOn(false);
    window.setTimeout(() => {
      setSheet(false);
      setQ("");
      setItems([]);
      setDropOpen(false);
    }, 180);
  }, []);

  // Hardware Back closes the sheet instead of leaving the page.
  useBackToClose(sheet, closeSheet);

  const imageErrorFor = useCallback((e: unknown) => {
    const code = (e as Error & { code?: string }).code;
    return t(
      code === "file_too_large" ? "listings.magic.image.too_large"
        : code === "image_search_unavailable" ? "listings.magic.image.unavailable"
          : code === "image_search_rate_limited" ? "listings.magic.image.rate_limited"
            : code === "link_refused" || code === "link_unreachable" ? "listings.magic.image.link_failed"
              : "listings.magic.image.error",
    );
  }, [t]);
  const leasedFilter = useCallback((): boolean | null => {
    const leased = searchParams.get("leased");
    return leased === "true" ? true : leased === "false" ? false : null;
  }, [searchParams]);
  const clearImage = useCallback(() => {
    setImageResult(null);
    setImageError(null);
    setImageNote(null);
    showPreview(null, false);
  }, [showPreview]);
  const takeImageResult = useCallback((res: ImageSearchResponse) => {
    setImageResult(res);
    captureEvent("image_search_result", {
      residence: res.residence?.cluster_id ?? null, candidates: res.candidates.length,
      match: res.match?.id ?? null, similar: res.similar.length, scene: res.scene,
      off_topic: res.off_topic ?? false,
    });
  }, []);
  const runImage = useCallback(async (file: Blob) => {
    if (!file.type.startsWith("image/")) return;
    showPreview(URL.createObjectURL(file), true);
    setDropOpen(false);
    setImageBusy(true);
    setImageError(null);
    setImageResult(null);
    // The answer shows where search answers show: the panel on desktop,
    // the full-screen search sheet on a phone.
    if (!sheet) { if (mobileVp) openSheet(); else setOpen(true); }
    captureEvent("image_search", { size: file.size, surface: sheet ? "sheet" : "bar" });
    try {
      // A phone photo is shrunk here before it travels (see prepareUpload):
      // the model sees 384 px anyway, and the visitors are on mobile data.
      const prepared = await prepareUpload(file);
      takeImageResult(await searchByImage(prepared, { leased: leasedFilter() }));
    } catch (e) {
      setImageError(imageErrorFor(e));
    } finally {
      setImageBusy(false);
    }
  }, [imageErrorFor, leasedFilter, mobileVp, openSheet, sheet, showPreview, takeImageResult]);
  /** A pasted link to an image: the backend fetches it (public hosts
   *  only) and searches it like an upload. */
  const runImageUrl = useCallback(async (url: string) => {
    showPreview(url, false);
    setDropOpen(false);
    setImageBusy(true);
    setImageError(null);
    setImageResult(null);
    setQ("");
    setItems([]);
    if (!sheet) { if (mobileVp) openSheet(); else setOpen(true); }
    captureEvent("image_search", { link: true, surface: sheet ? "sheet" : "bar" });
    try {
      takeImageResult(await searchByImageUrl(url, { leased: leasedFilter() }));
    } catch (e) {
      setImageError(imageErrorFor(e));
    } finally {
      setImageBusy(false);
    }
  }, [imageErrorFor, leasedFilter, mobileVp, openSheet, sheet, showPreview, takeImageResult]);
  /** A photo that arrived (pasted, dropped): one readable one is searched;
   *  several take the first and say so; none readable opens the zone with
   *  the formats it takes. True when the event was about a photo. */
  const takePhotos = (files: ArrayLike<File> | null | undefined): boolean => {
    if (!photoSearch) return false;
    const { file, extra, unsupported } = pickPhoto(files);
    if (file) { setImageNote(extra ? "one_at_a_time" : null); void runImage(file); return true; }
    if (unsupported) { setImageNote("format"); setDropOpen(true); return true; }
    return false;
  };
  const onPasteImage = (e: React.ClipboardEvent) => {
    if (takePhotos(e.clipboardData?.files)) { e.preventDefault(); return; }
    // A pasted link to a picture is a picture, not a sentence.
    const text = (e.clipboardData?.getData("text") ?? "").trim();
    if (photoSearch && text && looksLikeImageLink(text)) { e.preventDefault(); setImageNote(null); void runImageUrl(text); }
  };
  const onDropImage = (e: React.DragEvent) => {
    if (takePhotos(e.dataTransfer?.files)) e.preventDefault();
  };

  // The street chips' names ("Nizami küçəsi · Nəsimi", GET /api/streets/{id}),
  // fetched once per id and kept for the session.
  const streetIdsKey = searchParams.getAll("street").join(",");
  const [streetNames, setStreetNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const missing = (streetIdsKey ? streetIdsKey.split(",") : []).filter((id) => !(id in streetNames));
    if (!missing.length) return;
    let live = true;
    Promise.all(missing.map((id) =>
      fetchAPI<{ name: string; place?: string | null }>(`/streets/${encodeURIComponent(id)}`)
        .then((d) => [id, d.place ? `${d.name} · ${d.place}` : d.name] as const)
        .catch(() => [id, ""] as const),
    )).then((pairs) => { if (live) setStreetNames((cur) => ({ ...cur, ...Object.fromEntries(pairs) })); });
    return () => { live = false; };
    // streetNames is read, not watched: only new ids trigger a fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streetIdsKey]);

  // ── Applied filter chips (the editable state) ──────────────────
  const applied: AppliedChip[] = useMemo(() => {
    const chips: AppliedChip[] = [];
    const label = (key: string, v: string): string | null => {
      switch (key) {
        case "rooms":
          return t("listings.magic.chip.rooms").replace("{n}", v);
        case "tags": {
          const def = names?.vocab.tags.find((x) => x.id === v);
          return def
            ? def.labels[locale as keyof typeof def.labels] ?? def.labels.az
            : null;
        }
        case "location_ids":
          return names?.loc[v] ?? null;
        case "street":
          return streetNames[v] || t("listings.magic.chip.street");
        case "city":
          // The town's own name, as every place name is shown.
          return v;
        case "complex_slug":
          return names?.res[v] ?? null;
        case "developer":
          return names?.dev[v] ?? null;
        case "exclude_location_ids":
          return names?.loc[v] ? t("listings.applied.except").replace("{name}", names.loc[v]) : null;
        case "exclude_city":
          return t("listings.applied.except").replace("{name}", v);
        case "exclude_tags": {
          const def = names?.vocab.tags.find((x) => x.id === v);
          return def
            ? t("listings.applied.except").replace("{name}", def.labels[locale as keyof typeof def.labels] ?? def.labels.az)
            : null;
        }
        case "price_min":
          return t("listings.magic.chip.price_min").replace("{v}", fmtNum(v));
        case "price_max":
          return t("listings.magic.chip.price_max").replace("{v}", fmtNum(v));
        case "area_min":
          return t("listings.magic.chip.area_min").replace("{v}", fmtNum(v));
        case "area_max":
          return t("listings.magic.chip.area_max").replace("{v}", fmtNum(v));
        case "q":
          return t("listings.magic.chip.q").replace("{v}", v);
        case "rent_period":
          return v === "daily" || v === "room" || v === "all"
            ? t(`listings.filter.rent_period.${v}` as const)
            : null;
        case "metro_walk":
          return `${t("listings.filter.metro_walk")}: ${t("listings.filter.metro_walk.min").replace("{n}", v)}`;
        case "min_yield":
          return t("listings.filter.min_yield.chip").replace("{n}", v);
        case "leased":
          return t(v === "true"
            ? "listings.filter.transaction.rent"
            : "listings.filter.transaction.sale");
        case "category":
          return categoryKey(v) ? t(categoryKey(v)!) : null;
        case "building_type":
          return v === "new"
            ? t("listings.filter.building_type.new")
            : t("listings.filter.building_type.old");
        default:
          return null;
      }
    };
    for (const key of MULTI_KEYS) {
      for (const v of searchParams.getAll(key)) {
        const l = label(key, v);
        if (!l) continue;
        const rest = searchParams.getAll(key).filter((x) => x !== v);
        chips.push({
          id: `${key}:${v}`,
          label: l,
          remove: { [key]: rest.length ? rest : null },
          restore: { [key]: [v] },
        });
      }
    }
    for (const key of SCALAR_KEYS) {
      const v = searchParams.get(key);
      if (!v) continue;
      const l = label(key, v);
      if (l) {
        chips.push({
          id: key,
          label: l,
          ...(key === "q" ? { icon: "text" as const, title: t("listings.magic.chip.q_title") } : {}),
          remove: { [key]: null },
          restore: { [key]: v },
        });
      }
    }
    // Paired range keys render as ONE chip (a "5-ci mərtəbə" parse sets
    // min and max together; two ≥/≤ chips would read like noise).
    const pair = (
      minKey: string, maxKey: string,
      oneKey: Parameters<typeof t>[0], rangeKey: Parameters<typeof t>[0],
    ) => {
      const a = searchParams.get(minKey);
      const b = searchParams.get(maxKey);
      if (!a && !b) return;
      const l =
        a && b && a === b
          ? t(oneKey).replace("{n}", a)
          : t(rangeKey).replace("{r}", a && b ? `${a}-${b}` : a ? `≥\u00a0${a}` : `≤\u00a0${b}`);
      const restore: Record<string, string> = {};
      if (a) restore[minKey] = a;
      if (b) restore[maxKey] = b;
      chips.push({
        id: minKey,
        label: l,
        remove: { [minKey]: null, [maxKey]: null },
        restore,
      });
    };
    pair("floor_min", "floor_max",
      "listings.magic.chip.floor_one", "listings.magic.chip.floor_range");
    pair("total_floors_min", "total_floors_max",
      "listings.magic.chip.storeys_one", "listings.magic.chip.storeys_range");
    // A search by photo's lookalikes opened as the list (?photo=): one
    // chip; removing it drops its "best match first" order too.
    const photo = searchParams.get("photo");
    if (photo) {
      const lookSort = searchParams.get("sort") === "look";
      chips.push({
        id: "photo",
        label: t("listings.magic.chip.photo"),
        remove: lookSort ? { photo: null, sort: null } : { photo: null },
        restore: lookSort ? { photo, sort: "look" } : { photo },
      });
    }
    return chips;
  }, [searchParams, names, t, locale, streetNames]);

  // Every applied filter, for the phone sheet's "Seçilmiş filtrlər": the
  // filter-row values first, then the bar's own chips.
  const appliedAll: AppliedChip[] = useMemo(() => {
    const chips: AppliedChip[] = [];
    const lbl = (key: string, v: string): string | null => {
      switch (key) {
        case "location_ids": return names?.loc[v] ?? null;
        case "complex_slug": return names?.res[v] ?? null;
        case "rooms": return t("listings.magic.chip.rooms").replace("{n}", v);
        case "listing_kind":
          return v === "house" ? t("listings.filter.category.house")
            : v === "new" ? t("listings.filter.building_type.new")
            : t("listings.filter.building_type.old");
        case "street": return streetNames[v] || t("listings.magic.chip.street");
        case "leased": return t(v === "true" ? "listings.filter.transaction.rent" : "listings.filter.transaction.sale");
        case "category": return categoryKey(v) ? t(categoryKey(v)!) : null;
        case "property_kind": return t(kindChipKey(v));
        case "building_type": return v === "new" ? t("listings.filter.building_type.new") : t("listings.filter.building_type.old");
        case "price_min": return t("listings.magic.chip.price_min").replace("{v}", fmtNum(v));
        case "price_max": return t("listings.magic.chip.price_max").replace("{v}", fmtNum(v));
        default: return null;
      }
    };
    for (const key of SHEET_MULTI_KEYS) {
      if (key === "rooms") {
        const rc = roomsChip(searchParams.getAll("rooms"), t);
        if (rc) chips.push(rc);
        continue;
      }
      for (const v of searchParams.getAll(key)) {
        const l = lbl(key, v);
        if (!l) continue;
        const rest = searchParams.getAll(key).filter((x) => x !== v);
        chips.push({ id: `${key}:${v}`, label: l, remove: { [key]: rest.length ? rest : null }, restore: { [key]: [v] } });
      }
    }
    for (const key of SHEET_SCALAR_KEYS) {
      const v = searchParams.get(key);
      if (!v) continue;
      const l = lbl(key, v);
      if (l) chips.push({ id: key, label: l, remove: { [key]: null }, restore: { [key]: v } });
    }
    return [...chips, ...applied];
  }, [applied, searchParams, names, t, streetNames]);

  // Chips removed via empty-input Backspace, restorable with Ctrl+Z.
  // Deliberately NOT fed by ×-click removals: pointer removals have a
  // visible, repeatable path back (pick it again), while a keyboard
  // slip deserves a keyboard undo.
  const undoStack = useRef<AppliedChip[]>([]);

  function removeChip(chip: AppliedChip) {
    onReplaceOne(chip.remove);
  }

  // ── Quick picks (idle panel content) ───────────────────────────
  const quickPlaces: SearchSuggestion[] = useMemo(
    () =>
      (names?.rayons ?? [])
        .filter((r) => !searchParams.getAll("location_ids").includes(r.id))
        .map((r) => ({
          kind: "location" as const,
          id: r.id,
          label: { az: r.name, ru: r.name, en: r.name },
        })),
    [names, searchParams],
  );
  // Idle amenities: a curated "wow" set (sea view, panoramic windows,
  // pool, terrace, penthouse, smart home, designer renovation, sauna),
  // not the frequency-ranked list - the frequent ones are mundane
  // (furnished, combi, security) and made the empty panel read like a
  // utilities checklist (owner, 2026-09-20). Any tag missing from the
  // vocabulary is skipped; already-applied tags drop out.
  const { budget: affordBudget } = useAffordability();
  const quickTags: SearchSuggestion[] = useMemo(() => {
    const chosen = new Set(searchParams.getAll("tags"));
    const out: SearchSuggestion[] = [];
    // "Within my budget" leads the idle picks once the affordability
    // profile exists: it is just a price ceiling, so it rides the
    // existing price token and the price chip pulses.
    if (affordBudget && affordBudget.maxPrice > 0 && !searchParams.get("price_max")) {
      out.push({
        kind: "price",
        id: "afford:max",
        label: { az: "Büdcəmə uyğun", ru: "По моему бюджету", en: "Within my budget" },
        sub: {
          az: `${affordBudget.maxPrice.toLocaleString("ru-RU")} AZN-ə qədər`,
          ru: `до ${affordBudget.maxPrice.toLocaleString("ru-RU")} AZN`,
          en: `up to ${affordBudget.maxPrice.toLocaleString("ru-RU")} AZN`,
        },
        apply: { price_max: String(affordBudget.maxPrice) },
      });
    }
    for (const id of WOW_TAGS) {
      if (chosen.has(id)) continue;
      const def = names?.vocab.tags.find((x) => x.id === id);
      if (def) out.push({ kind: "tag", id, label: def.labels });
      if (out.length >= 8) break;
    }
    return out;
  }, [names, searchParams, affordBudget]);

  function pick(s: SearchSuggestion) {
    // Desktop: the pick lands in the filter row UNDER this panel, so the
    // panel closes and that chip pulses once - otherwise the choice looks
    // lost (owner, 2026-09-20). The sheet stays open and lists it instead.
    const pulse = PULSE_TARGET[s.kind];
    if (!sheet) {
      setOpen(false);
      if (pulse) {
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent("menzil:filter-pulse", { detail: { target: pulse } }));
        }, 50);
      }
    }
    // Usage analytics (owner, 2026-09-19): every way the smart bar is
    // used gets its own event so the feature's value can be measured.
    captureEvent("magic_search_pick", { kind: s.kind, surface: sheet ? "sheet" : "bar", typed: q.trim().length > 0 });
    if (s.apply) onMergeOne(s.apply);
    else if (s.kind === "location") onMergeOne({ location_ids: [s.id] });
    else if (s.kind === "residence") onMergeOne({ complex_slug: [s.id] });
    else onMergeOne({ tags: [s.id] });
    // Drop the consumed token, keep the panel open for the next pick -
    // the applied chips above show what just landed.
    setQ("");
    setItems([]);
    setHint(null);
  }

  /** A pasted listing link's row: open the listing where this page opens
   *  listings, or analyze a listing the index does not hold. While the
   *  lookup is out, Enter waits for it. */
  function runLink(r: LinkRow) {
    if (r.kind === "loading") {
      linkEnterPending.current = true;
      return;
    }
    trackEvent("search.link", {
      outcome: r.kind === "found" ? "open" : "analyze",
      why: r.kind === "missing" ? r.why : null,
      site: sourceHostLabel(r.url),
      surface: sheet ? "sheet" : "bar",
    });
    const go = r.kind === "found"
      ? () => openListingRef.current?.(r.card)
      : () => router.push(`/analyze?url=${encodeURIComponent(r.url)}`);
    setQ("");
    setItems([]);
    setHint(null);
    setOpen(false);
    navigated.current = false;
    // On a phone the sheet slides away first, so the drawer (or the next
    // page) takes the screen with its own Back step instead of one
    // stacked on the sheet's (lib/use-back-to-close).
    if (sheet) {
      closeSheet();
      window.setTimeout(go, 200);
    } else {
      go();
    }
  }

  /** A link on a home bar: to the valuation (the host's onEvaluateLink). */
  function runEvaluate(url: string) {
    const go = evaluateRef.current;
    if (!go) return;
    trackEvent("search.link", { outcome: "evaluate", why: null, site: sourceHostLabel(url), surface: sheet ? "sheet" : "bar" });
    setQ("");
    setItems([]);
    setHint(null);
    setOpen(false);
    navigated.current = false;
    if (sheet) {
      closeSheet();
      window.setTimeout(() => go(url), 200);
    } else {
      go(url);
    }
  }

  // An Enter that waited for the lookup runs the row once it lands.
  useEffect(() => {
    if (!linkEnterPending.current || !linkAnswer || linkAnswer.url !== link) return;
    linkEnterPending.current = false;
    runLink(linkAnswer);
    // runLink reads this render's state; only a landed answer runs it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkAnswer]);

  /** Keyword search: the typed words become a `q` filter over the
   *  description text (merged into the current filters). Used directly
   *  when the AI parser is off, and as the fallback when the parser
   *  finds no structured filter in the sentence. */
  function applyKeyword(query: string, outcome: "keyword" | "keyword_fallback") {
    if (looksLikeImageLink(query)) {
      void runImageUrl(query.trim());
      return;
    }
    if (onLink && looksLikeLink(query) && onLink(query)) {
      setQ("");
      setItems([]);
      setOpen(false);
      if (sheet) closeSheet();
      return;
    }
    // "Bakı", "evlər", "elanlar" name everything we list: no filter, and
    // no search of the descriptions for the word either.
    if (isEverythingQuery(query)) {
      trackEvent("search.query", { q: query.slice(0, 140), outcome: "everything", surface: sheet ? "sheet" : "bar" });
      setQ("");
      setOpen(false);
      if (sheet) closeSheet();
      return;
    }
    // The plain words first: "rent" is a filter, not a word to look for
    // in descriptions (owner, 2026-10-05).
    const plain = plainParams(query);
    if (Object.keys(plain).length) {
      captureEvent("magic_search_parse", { outcome: "plain", chars: query.length, surface: sheet ? "sheet" : "bar", params: Object.keys(plain) });
      trackEvent("search.query", { q: query.slice(0, 140), outcome: "plain", params: Object.keys(plain).sort().join(","), surface: sheet ? "sheet" : "bar" });
      onApplyAll(plain);
      setQ("");
      setOpen(false);
      if (sheet) closeSheet();
      return;
    }
    captureEvent("magic_search_parse", {
      outcome, chars: query.length, surface: sheet ? "sheet" : "bar",
    });
    // The sentence itself, first-party. The capture above counts parses
    // but throws the words away, and the words are the point (owner,
    // 2026-09-22: "I can see in which direction people are going and
    // what they are thinking about").
    trackEvent("search.query", {
      q: query.slice(0, 140),
      outcome,
      surface: sheet ? "sheet" : "bar",
    });
    onMergeOne({ q: query.slice(0, 120) });
    setQ("");
    setOpen(false);
    if (sheet) closeSheet();
  }

  async function parseSentence() {
    const query = q.trim();
    if (query.length < 2 || busy) return;
    if (looksLikeImageLink(query)) {
      void runImageUrl(query.trim());
      return;
    }
    if (onLink && looksLikeLink(query) && onLink(query)) {
      setQ("");
      setItems([]);
      setOpen(false);
      if (sheet) closeSheet();
      return;
    }
    if (!aiEnabled) {
      applyKeyword(query, "keyword");
      return;
    }
    if (query.length < 3) return;
    setBusy(true);
    setHint(null);
    try {
      const res = await magicSearch(query);
      if (!res.params || Object.keys(res.params).length === 0) {
        // No structured filter in the sentence: search the descriptions
        // for the words instead of reporting a dead end.
        applyKeyword(query, "keyword_fallback");
        return;
      }
      // Words the parser could not map become a keyword filter (a
      // street name, a brand, "penthouse") instead of a warning.
      const params: Record<string, string | string[]> = { ...res.params };
      const rest = (res.unparsed || "").trim();
      const restAsKeyword = rest.length >= 2 && rest.length <= 60;
      if (restAsKeyword) params.q = rest;
      const outcome = (res.resolved_locations || []).some((l) => !l.resolved) || res.unparsed ? "partial" : "full";
      captureEvent("magic_search_parse", {
        outcome,
        chars: query.length,
        params: Object.keys(params),
        surface: sheet ? "sheet" : "bar",
      });
      trackEvent("search.query", {
        q: query.slice(0, 140),
        outcome,
        params: Object.keys(params).sort().join(","),
        unparsed: rest.slice(0, 60) || null,
        surface: sheet ? "sheet" : "bar",
      });
      onApplyAll(params);
      setQ("");
      setOpen(false);
      if (sheet) closeSheet();
      const misses = (res.resolved_locations || [])
        .filter((l) => !l.resolved)
        .map((l) => l.query);
      if (misses.length || (rest && !restAsKeyword)) {
        setHint(
          t("listings.magic.partial").replace(
            "{rest}",
            [...misses, restAsKeyword ? "" : rest].filter(Boolean).join(", "),
          ),
        );
      }
    } catch (e) {
      // Throttled parse (paid LLM call) - tell the user to slow down
      // instead of claiming the feature is broken.
      const limited =
        e instanceof Error && e.message.includes("magic_rate_limited");
      setHint(t(limited ? "listings.magic.rate_limited" : "listings.magic.unavailable"));
    } finally {
      setBusy(false);
    }
  }

  // ONE action row under the suggestions - "search this" (owner,
  // 2026-09-20: no separate "ask the AI" option). What it does depends
  // only on the text: a word or a quoted phrase searches the listing
  // text and says so; a sentence (two words or more, with the parser
  // enabled) is read for filters first - rooms, price, place, amenities
  // - and whatever is left over becomes the text search, all shown as
  // chips. The row shares one keyboard index space with the suggestions
  // ([...items, action]); Enter runs the highlighted row and the first
  // row is highlighted by default, so what Enter does is visible.
  const trimmed = q.trim();
  const typing = trimmed.length >= 2;
  const exact = /["«»“”„]/.test(trimmed);
  const sentence = aiEnabled && !exact && trimmed.split(/\s+/).length >= 2;
  // A link on a home bar: one row, the valuation (onEvaluateLink).
  const evaluating = !!onEvaluateLink && typing && isEvaluable(trimmed);
  const actions: { key: "search" | "kw"; label: string; run: () => void }[] = typing && !linkRow && !evaluating
    ? [
        sentence
          ? {
              key: "search" as const,
              label: t(busy ? "listings.magic.parsing" : "listings.magic.search_cta").replace("{q}", trimmed),
              run: parseSentence,
            }
          : {
              key: "kw" as const,
              label: t(exact ? "listings.magic.keyword_exact_cta" : "listings.magic.keyword_cta").replace("{q}", trimmed),
              run: () => applyKeyword(trimmed, "keyword"),
            },
      ]
    : [];
  // A pasted listing link has its own one row (linkSection), not these.
  const rowCount = linkRow || evaluating ? 0 : items.length + actions.length;
  const activeRow = rowCount === 0 ? -1 : active >= 0 && active < rowCount ? active : 0;

  function runRow(i: number) {
    if (i < 0 || busy) return;
    if (i < items.length) pick(items[i]);
    else actions[i - items.length]?.run();
  }

  // Enter after arrow keys runs the highlighted row; Enter straight after
  // typing a phrase searches the phrase. Picking the first suggestion
  // instead (often a chip already applied) looked like a dead Go key on
  // phones (owner, 2026-10-12).
  const navigated = useRef(false);

  /** Enter on one word: the top suggestion when it names what was typed
   *  (the place, residence, amenity or chip), fetched first when the
   *  panel has not caught up; otherwise the plain words or a search of
   *  the descriptions. "Yasamal" + Enter searched the descriptions for
   *  "Yasamal" 7 times out of 7 (usage review, 2026-10-20). */
  async function enterWord(word: string) {
    let rows = itemsFor.current === word ? items : null;
    if (!rows) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      try {
        rows = await getSearchSuggestions(word);
      } catch {
        rows = [];
      }
      // Typed on while the lookup was out: the newer text decides.
      if (qNow.current.trim() !== word) return;
      setItems(rows);
      itemsFor.current = word;
    }
    const top = rows[0];
    // "fuzzy": one typing mistake from exactly one word of a name we hold
    // ("Nargle", "Yasmal"), trusted like the start of a name.
    const trusted = !!top && (
      LITERAL_KINDS.has(top.kind) || top.match === "exact"
      || ((top.match === "start" || top.match === "fuzzy") && top.kind !== "street")
    );
    if (trusted) {
      trackEvent("search.query", { q: word.slice(0, 140), outcome: "suggestion", params: top.kind, surface: sheet ? "sheet" : "bar" });
      pick(top);
      if (sheet) closeSheet();
    } else {
      applyKeyword(word, "keyword");
    }
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") navigated.current = true;
    if (e.key === "ArrowDown" && !open && !sheet) {
      // Closed after a pick: the arrow reopens the panel first.
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown" && rowCount) {
      e.preventDefault();
      setActive((activeRow + 1) % rowCount);
    } else if (e.key === "ArrowUp" && rowCount) {
      e.preventDefault();
      setActive(activeRow <= 0 ? rowCount - 1 : activeRow - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // A link on a home bar goes to the valuation.
      if (evaluating) {
        runEvaluate(trimmed);
        return;
      }
      // A listing link runs its one row: open the listing, or analyze it
      // (once the lookup lands when it is still out).
      if (linkRow) {
        navigated.current = false;
        runLink(linkRow);
        return;
      }
      // A pasted link goes where it belongs before any row is picked: a
      // suggestion row would otherwise take the Enter (owner, 2026-10-11).
      if (looksLikeImageLink(trimmed)) {
        void runImageUrl(trimmed);
        return;
      }
      if (onLink && looksLikeLink(trimmed) && onLink(trimmed)) {
        setQ("");
        setItems([]);
        setOpen(false);
        if (sheet) closeSheet();
        return;
      }
      if (sheet && !typing) {
        // Go on an empty sheet means "show me the results".
        closeSheet();
        return;
      }
      // A sentence goes to the parser; a single word keeps the top
      // suggestion when it names what was typed (enterWord).
      const phrase = typing && !navigated.current ? actions.find((a) => a.key === "search") : undefined;
      const oneWord = typing && !navigated.current && !phrase && !exact && !/\s/.test(trimmed);
      if (phrase && !busy) {
        phrase.run();
      } else if (oneWord && !busy) {
        navigated.current = false;
        void enterWord(trimmed);
        return;
      } else {
        runRow(activeRow);
      }
      navigated.current = false;
      if (sheet && !(phrase && phrase.key === "search")) closeSheet();
    } else if (e.key === "Backspace" && q === "" && applied.length > 0) {
      // Token-field behaviour: empty input + Backspace eats the last
      // applied chip; Ctrl/Cmd+Z below brings it back.
      e.preventDefault();
      const last = applied[applied.length - 1];
      undoStack.current.push(last);
      removeChip(last);
    } else if (
      (e.key === "z" || e.key === "Z") &&
      (e.metaKey || e.ctrlKey) &&
      undoStack.current.length > 0
    ) {
      e.preventDefault();
      const chip = undoStack.current.pop()!;
      onMergeOne(chip.restore);
    } else if (e.key === "Escape") {
      if (dropOpen) setDropOpen(false);
      else if (sheet) closeSheet();
      else setOpen(false);
    }
  }

  const label = (s: SearchSuggestion) =>
    s.label[locale as keyof typeof s.label] ?? s.label.az;
  const sub = (s: SearchSuggestion) =>
    s.sub?.[locale as keyof typeof s.sub] ?? s.sub?.az ?? "";

  // ── Shared panel pieces ────────────────────────────────────────

  const row = (s: SearchSuggestion, i: number, large: boolean) => {
    const Icon = KIND_ICON[s.kind] ?? Tag;
    const subText = sub(s);
    return (
      <button
        key={`${s.kind}:${s.id}`}
        type="button"
        onMouseDown={(e) => { e.preventDefault(); pick(s); }}
        onMouseEnter={() => !large && i >= 0 && setActive(i)}
        className={`no-scale flex w-full items-center text-left ${
          large
            ? "gap-3 px-4 py-3 text-[15px] hover:bg-gray-50 active:bg-gray-100"
            : `gap-2.5 px-3.5 py-2 text-sm hover:bg-gray-50 ${i >= 0 && i === activeRow ? "bg-gray-50" : ""}`
        }`}
      >
        {s.kind === "developer" ? (
          <DeveloperLogo name={label(s)} logo={s.logo} size={24} />
        ) : (
          <Icon
            className={`shrink-0 text-gray-400 ${large ? "h-[18px] w-[18px]" : "h-4 w-4"}`}
            strokeWidth={2}
            aria-hidden="true"
          />
        )}
        <span className="min-w-0 truncate text-gray-900">{label(s)}</span>
        {subText && (
          <span className="shrink-0 text-xs text-gray-400">{subText}</span>
        )}
      </button>
    );
  };

  // ── A pasted listing link: that listing, or the analysis ───────
  // One row, highlighted like the first suggestion: Enter or a tap runs
  // it. The found listing shows what its card shows (photo, price,
  // facts, place), so the reader sees it is the right one before it opens.
  const linkSection = (large: boolean) => {
    if (!linkRow) return null;
    const pad = large ? "px-4" : "px-3.5";
    const iconCls = `shrink-0 ${large ? "h-[18px] w-[18px]" : "h-4 w-4"}`;
    const rowCls = `no-scale flex w-full items-center text-left ${
      large ? "gap-3 px-4 py-3 text-[15px] active:bg-gray-50" : "gap-2.5 px-3.5 py-2 text-sm bg-gray-50 hover:bg-gray-100"
    }`;
    const enterKey = !large && (
      <kbd className="shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400">↵</kbd>
    );
    if (linkRow.kind === "loading") {
      return (
        <div
          role="status"
          className={`flex items-center text-gray-500 ${large ? "gap-3 px-4 py-3 text-[15px]" : "gap-2.5 px-3.5 py-2 text-sm"}`}
        >
          <Loader2 className={`${iconCls} animate-spin text-gray-400`} aria-hidden="true" />
          <span className="min-w-0 truncate">{t("listings.magic.link.looking")}</span>
        </div>
      );
    }
    if (linkRow.kind === "found") {
      const c = linkRow.card;
      const land = c.category === "land";
      // An office or a garage reads by its kind, not a room count.
      const separate = isSeparate(c.category);
      const facts = [
        land
          ? c.land_area_sot != null && t("listings.card.sot").replace("{n}", String(c.land_area_sot))
          : separate
            ? t(kindNounKey(c.category, c.property_kind))
            : c.room_count != null && t("listings.card.rooms").replace("{n}", String(c.room_count)),
        !land && c.area_m2 != null && t("listings.card.area").replace("{n}", String(Math.round(c.area_m2))),
        !land && c.category !== "garage" && c.floor != null && c.total_floors != null && `${c.floor}/${c.total_floors}`,
      ].filter(Boolean).join(" · ");
      const price = c.price != null ? `${formatPrice(c.price)} ${currencySign(c.currency)}` : null;
      const place = c.complex_name ?? c.district ?? c.metro ?? c.sub_location ?? c.location_name ?? c.city_name ?? null;
      return (
        <>
          <p className={`pt-2.5 pb-1 text-xs font-semibold text-gray-500 ${pad}`}>
            {t("listings.magic.link.found").replace("{site}", sourceHostLabel(c.source_url, c.source))}
          </p>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); runLink(linkRow); }}
            aria-label={[t("listings.magic.link.open"), price, facts, place].filter(Boolean).join(", ")}
            className={rowCls}
          >
            <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-100">
              {c.thumbnail_url ? (
                <Photo
                  src={c.thumbnail_url}
                  className="h-full w-full object-cover"
                  fallback={(
                    <span className="flex h-full w-full items-center justify-center">
                      <Building2 className="h-4 w-4 text-gray-400" strokeWidth={2} aria-hidden="true" />
                    </span>
                  )}
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center">
                  <Building2 className="h-4 w-4 text-gray-400" strokeWidth={2} aria-hidden="true" />
                </span>
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-gray-900">
                {price && <span className="font-semibold tabular-nums">{price}</span>}
                {price && facts && <span className="text-gray-400"> · </span>}
                {facts && <span className="tabular-nums">{facts}</span>}
              </span>
              {place && <span className="block truncate text-xs text-gray-500">{place}</span>}
            </span>
            {enterKey}
          </button>
        </>
      );
    }
    return (
      <>
        <p className={`flex items-start gap-1.5 pt-2.5 pb-1.5 text-xs text-gray-500 ${pad}`}>
          <Info className="mt-px h-3.5 w-3.5 shrink-0 text-gray-400" strokeWidth={2} aria-hidden="true" />
          {linkRow.why === "inactive"
            ? t("listings.magic.link.inactive")
            : linkRow.why === "error"
              ? t("listings.magic.link.error")
              : t("listings.magic.link.absent")}
        </p>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); runLink(linkRow); }}
          className={`${rowCls} border-t border-gray-100`}
        >
          <Sparkles className={`${iconCls} text-gray-400`} strokeWidth={2} aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-gray-700">{t("listings.magic.link.analyze")}</span>
          {enterKey}
        </button>
      </>
    );
  };

  // ── A link on a home bar: one row, the valuation ─────────────────
  const evaluateSection = (large: boolean) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); runEvaluate(trimmed); }}
      className={`no-scale flex w-full items-center text-left ${
        large ? "gap-3 px-4 py-3 text-[15px] active:bg-gray-50" : "gap-2.5 bg-gray-50 px-3.5 py-2 text-sm hover:bg-gray-50"
      }`}
    >
      <Sparkles className={`shrink-0 text-gray-400 ${large ? "h-[18px] w-[18px]" : "h-4 w-4"}`} strokeWidth={2} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-gray-700">
        {t("listings.magic.link.evaluate").replace("{site}", sourceHostLabel(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`))}
      </span>
      {!large && (
        <kbd className="shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400">↵</kbd>
      )}
    </button>
  );

  const sectionHead = (text: string, large: boolean) => (
    <div className={`pt-3 pb-1 text-xs font-semibold text-gray-500 ${large ? "px-4" : "px-3.5"}`}>
      {text}
    </div>
  );

  const appliedSection = (large: boolean) =>
    (large ? appliedAll : applied).length > 0 && (
      <div className={`border-b border-gray-100 pb-2.5 ${large ? "px-4" : "px-3.5"}`}>
        <div className="pt-2.5 pb-1.5 text-xs font-semibold text-gray-500">
          {t("listings.magic.applied")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(large ? appliedAll : applied).map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); removeChip(c); }}
              className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 py-1 pl-2.5 pr-1.5 text-xs font-medium text-gray-800 hover:border-gray-400"
              aria-label={`${c.title ? c.title + ": " : ""}${c.label} - ${t("listings.filter.reset")}`}
              title={c.title}
            >
              {c.icon === "text" && <TextSearch className="h-3.5 w-3.5 shrink-0 text-gray-500" strokeWidth={2} aria-hidden="true" />}
              {c.label}
              <X className="h-3.5 w-3.5 rounded-full text-gray-400 group-hover:text-gray-900" strokeWidth={2} />
            </button>
          ))}
        </div>
      </div>
    );

  // Suggestions sit at the top of the panel whatever is typed (owner,
  // 2026-10-01: "suggestions should always be visible in the dropdown").
  const adviceSection = (large: boolean) => (
    <>
      {advice.length > 0 && (
            <div className="border-b border-gray-100 pb-1">
              {sectionHead(t("listings.magic.suggested"), large)}
              {advice.map((r) => (
                <div key={r.key}>
                  <p className={`flex items-start gap-1.5 text-xs text-gray-500 ${large ? "px-4 pb-1" : "px-3.5 pb-0.5"}`}>
                    {r.tip ? (
                      <Lightbulb className="mt-px h-3.5 w-3.5 shrink-0 text-gray-400" strokeWidth={2} aria-hidden="true" />
                    ) : (
                      <Sparkles className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500" strokeWidth={2} aria-hidden="true" />
                    )}
                    <span>{r.text}</span>
                  </p>
                  {r.actions.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        a.onPick();
                        onReplaceOne(a.apply);
                        setOpen(false);
                      }}
                      // A tip (grey since the quiet UI) hovers grey, a fix
                      // (amber) hovers amber (owner, 2026-10-11: "if it's
                      // blue, why a yellowish background on hover?").
                      className={`no-scale flex w-full items-center text-left ${
                        large
                          ? `gap-3 px-4 py-2.5 text-[15px] ${r.tip ? "active:bg-gray-50" : "active:bg-amber-50"}`
                          : `gap-2.5 px-3.5 py-1.5 text-sm ${r.tip ? "hover:bg-gray-50" : "hover:bg-amber-50"}`
                      }`}
                    >
                      <span className={`shrink-0 ${large ? "w-[18px]" : "w-4"}`} aria-hidden="true" />
                      <span className={`min-w-0 truncate font-semibold tabular-nums ${r.tip ? "text-gray-900" : "text-amber-800"}`}>{a.label}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
    </>
  );

  // The photo search's answer at the top of the panel: the residence
  // (one confident row, or up to three candidates), then listings that
  // look like the photo. Picking a residence filters to it.
  const imageSection = (large: boolean) => {
    if (!imageBusy && !imageError && !imageResult) return null;
    const pad = large ? "px-4" : "px-3.5";
    const pick = (cid: string) => {
      captureEvent("image_search_pick", { cluster_id: cid });
      onMergeOne({ complex_slug: [cid] });
      setImageResult(null);
      showPreview(null, false);
      if (sheet) closeSheet(); else setOpen(false);
    };
    const residenceRow = (r: ResidenceMatch, lead: boolean) => (
      <button
        key={r.cluster_id}
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => pick(r.cluster_id)}
        className={`no-scale flex w-full items-center gap-3 ${pad} ${large ? "py-3" : "py-2"} text-left hover:bg-gray-50`}
      >
        <Building2
          className={`shrink-0 ${lead ? "text-gray-700" : "text-gray-400"} ${large ? "h-[18px] w-[18px]" : "h-4 w-4"}`}
          strokeWidth={2}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className={`block truncate ${lead ? "font-semibold text-gray-900" : "text-gray-700"} ${large ? "text-[15px]" : "text-sm"}`}>
            {r.name}
            {r.district ? <span className="font-normal text-gray-400"> · {r.district}</span> : null}
          </span>
          <span className="block text-xs text-gray-500 tabular-nums">
            {t("listings.magic.image.match").replace("{pct}", String(Math.round(r.sim * 100)))}
            {" · "}
            {t("listings.magic.image.listings").replace("{n}", String(r.live_sale_n + r.live_rent_n))}
          </span>
        </span>
      </button>
    );
    return (
      <div className="border-b border-gray-100 pb-1">
        {imagePreview && (
          <div className={`flex items-center gap-3 ${pad} py-2`}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imagePreview}
              alt={t("listings.magic.drop.your_photo")}
              className="h-14 w-20 shrink-0 rounded-lg bg-gray-100 object-cover"
              onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
            />
            <span className="min-w-0 flex-1">
              <span className={`flex items-center gap-2 ${large ? "text-[15px]" : "text-sm"} ${imageBusy ? "text-gray-500" : "font-medium text-gray-900"}`}>
                {imageBusy && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" aria-hidden="true" />}
                {t(imageBusy ? "listings.magic.image.busy" : "listings.magic.drop.your_photo")}
              </span>
              {imageNote === "one_at_a_time" && (
                <span className="mt-0.5 block text-xs text-amber-700">{t("listings.magic.drop.one_at_a_time")}</span>
              )}
            </span>
            {!imageBusy && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  clearImage();
                  setDropOpen(true);
                }}
                className="shrink-0 text-sm font-medium text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
              >
                {t("listings.magic.drop.another")}
              </button>
            )}
            {!imageBusy && (
              // Removing the photo returns the panel to a text search
              // (owner, 2026-10-21: it could only be swapped, not removed).
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  clearImage();
                  inputRef.current?.focus();
                }}
                aria-label={t("listings.magic.drop.remove")}
                title={t("listings.magic.drop.remove")}
                className="-mr-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {imageBusy && !imagePreview && (
          <div className={`flex items-center gap-2 ${pad} py-2 text-sm text-gray-500`}>
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />
            {t("listings.magic.image.busy")}
          </div>
        )}
        {imageError && <p className={`${pad} py-2 text-xs font-medium text-rose-700`}>{imageError}</p>}
        {imageResult?.off_topic && (
          <div className={`${pad} py-3`}>
            <p className={`${large ? "text-[15px]" : "text-sm"} font-medium text-gray-900`}>{t("listings.magic.image.off_topic")}</p>
            <p className="mt-0.5 text-sm text-gray-500">{t("listings.magic.image.off_topic_hint")}</p>
          </div>
        )}
        {imageResult && !imageResult.off_topic && (
          <>
            {imageResult.match && (() => {
              const m = imageResult.match;
              const lp = listingPath(m.id);
              return (
                <>
                  {sectionHead(t("listings.magic.image.match_listing"), large)}
                  <Link
                    href={lp ?? "/listings"}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => captureEvent("image_search_pick_listing", { id: m.id })}
                    className={`no-scale flex w-full items-center gap-3 ${pad} ${large ? "py-3" : "py-2"} text-left hover:bg-gray-50`}
                  >
                    <span className="block h-11 w-14 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      {(m.look_photo_url || m.thumbnail_url) ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={m.look_photo_url || m.thumbnail_url || ""} alt="" className="h-full w-full object-cover" loading="lazy" />
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate font-semibold text-gray-900 tabular-nums ${large ? "text-[15px]" : "text-sm"}`}>
                        {m.price != null ? `${formatPrice(m.price)} AZN` : t("common.na")}
                        {m.complex_name ? <span className="font-normal text-gray-500"> · {m.complex_name}</span> : null}
                      </span>
                      <span className="block truncate text-xs text-gray-500 tabular-nums">
                        {[
                          m.room_count ? t("listings.magic.chip.rooms").replace("{n}", String(m.room_count)) : null,
                          m.area_m2 ? `${Math.round(m.area_m2)} m²` : null,
                          m.district || m.location_name || m.city_name || null,
                        ].filter(Boolean).join(" · ")}
                        {" · "}
                        {t("listings.magic.image.match").replace("{pct}", String(Math.round(m.look_sim * 100)))}
                      </span>
                    </span>
                  </Link>
                </>
              );
            })()}
            {imageResult.residence && (
              <>
                {sectionHead(t("listings.magic.image.residence"), large)}
                {residenceRow(imageResult.residence, true)}
              </>
            )}
            {imageResult.candidates.length > 0 && (
              <>
                {sectionHead(t("listings.magic.image.candidates"), large)}
                {imageResult.candidates.map((r) => residenceRow(r, false))}
              </>
            )}
            {/* No residence and no listing: say why. A found listing is the
                answer by itself (owner's emlaktap photo, 2026-10-21). */}
            {!imageResult.residence && imageResult.candidates.length === 0 && !imageResult.match && (
              <p className={`${pad} py-2 text-xs text-gray-500`}>
                {t(imageResult.scene === "exterior" ? "listings.magic.image.none" : "listings.magic.image.not_exterior")}
              </p>
            )}
            {imageResult.similar.length > 0 && (
              <>
                {sectionHead(t("listings.magic.image.similar"), large)}
                {/* A few as rows; all of them as the page's own list (owner,
                    2026-10-21: a long strip is hard to keep track of). */}
                {imageResult.similar.slice(0, 4).map((c) => {
                  const lp = listingPath(c.id);
                  return (
                    <Link
                      key={c.id}
                      href={lp ?? "/listings"}
                      onMouseDown={(e) => e.preventDefault()}
                      className={`no-scale flex w-full items-center gap-3 ${pad} ${large ? "py-2.5" : "py-2"} text-left hover:bg-gray-50`}
                    >
                      <span className="block h-11 w-14 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                        {c.thumbnail_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={c.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate ${large ? "text-[15px]" : "text-sm"} font-semibold text-gray-900 tabular-nums`}>
                          {c.price != null ? `${formatPrice(c.price)} AZN` : t("common.na")}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {[
                            c.room_count ? t("listings.magic.chip.rooms").replace("{n}", String(c.room_count)) : null,
                            c.area_m2 ? `${Math.round(c.area_m2)} m²` : null,
                            c.complex_name || c.location_name || null,
                          ].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-gray-500">
                        {t("listings.magic.image.match").replace("{pct}", String(Math.round(c.look_sim * 100)))}
                      </span>
                    </Link>
                  );
                })}
              </>
            )}
            {imageResult.search_id && (imageResult.similar_total ?? 0) > 0 && (
              <div className={`${pad} pb-2 pt-1`}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    captureEvent("image_search_list", { n: imageResult.similar_total ?? 0 });
                    onMergeOne({ photo: imageResult.search_id as string, sort: "look" });
                    if (sheet) closeSheet(); else setOpen(false);
                  }}
                  className={`flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 ${large ? "py-3 text-[15px]" : "py-2 text-sm"} font-semibold text-gray-900 hover:border-gray-300`}
                >
                  {t("listings.magic.image.show_all").replace("{n}", String(imageResult.similar_total))}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const panelBody = (large: boolean) => (
    <>
      {/* In-panel applied chips only on the sheet - on desktop they
          already sit in the bar right above the panel. */}
      {large && appliedSection(large)}
      {hint && (
        <p className={`mt-2 flex items-start gap-1.5 text-xs font-medium text-amber-700 ${large ? "px-4" : "px-3.5"}`}>
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
          {hint}
        </p>
      )}
      {adviceSection(large)}
      {imageSection(large)}
      {evaluating ? (
        evaluateSection(large)
      ) : typing && linkRow ? (
        linkSection(large)
      ) : typing ? (
        <>
          {items.map((s, i) => row(s, i, large))}
          {items.length === 0 && (
            <div className={`text-gray-400 ${large ? "px-4 py-3 text-[15px]" : "px-3.5 py-2 text-sm"}`}>
              {t("listings.magic.no_matches")}
            </div>
          )}
          {actions.map((a, j) => {
            const i = items.length + j;
            const isActive = i === activeRow;
            const spinning = busy && a.key === "search";
            return (
              <button
                key={a.key}
                type="button"
                disabled={busy}
                aria-busy={spinning || undefined}
                onMouseDown={(e) => { e.preventDefault(); runRow(i); }}
                onMouseEnter={() => !large && setActive(i)}
                className={`no-scale flex w-full items-center border-t border-gray-100 text-left ${
                  large
                    ? "gap-3 px-4 py-3 text-[15px] active:bg-gray-50"
                    : `gap-2.5 px-3.5 py-2 text-sm hover:bg-gray-50 ${isActive ? "bg-gray-50" : ""}`
                } ${busy && !spinning ? "opacity-50" : ""}`}
              >
                {spinning ? (
                  <Loader2
                    className={`shrink-0 animate-spin text-gray-400 ${large ? "h-[18px] w-[18px]" : "h-4 w-4"}`}
                    aria-hidden="true"
                  />
                ) : a.key === "search" ? (
                  <Search
                    className={`shrink-0 text-gray-400 ${large ? "h-[18px] w-[18px]" : "h-4 w-4"}`}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                ) : (
                  <TextSearch
                    className={`shrink-0 text-gray-400 ${large ? "h-[18px] w-[18px]" : "h-4 w-4"}`}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                )}
                <span className={`min-w-0 flex-1 truncate ${spinning ? "text-gray-500" : "text-gray-700"}`}>
                  {a.label}
                </span>
                {!large && isActive && !busy && (
                  <kbd className="shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400">↵</kbd>
                )}
              </button>
            );
          })}
        </>
      ) : (
        <>
          {quickTags.length > 0 && (
            <>
              {sectionHead(t("listings.magic.quick_tags"), large)}
              {quickTags.map((s) => row(s, -1, large))}
            </>
          )}
          {quickPlaces.length > 0 && (
            <>
              {sectionHead(t("listings.magic.quick_places"), large)}
              {quickPlaces.map((s) => row(s, -1, large))}
            </>
          )}
        </>
      )}
    </>
  );

  const expanded = !compact || open || focusIn;
  const COMPACT_CHIPS = 1;
  const shownChips = compact && !expanded ? applied.slice(0, COMPACT_CHIPS) : applied;
  const hiddenChips = applied.length - shownChips.length;
  // With the chips outside the field (the list page), its X clears only
  // what is typed: the page's one clear-all is "Sıfırlayın" in the
  // results row (owner, 2026-10-29).
  const showClear = chipsOutside ? q.trim().length > 0 : (applied.length > 0 || q.trim().length > 0) && !!onClearAll;

  return (
    <div
      ref={boxRef}
      className={`relative ${compact ? "min-w-0 flex-1 sm:h-10" : chipsOutside ? "" : "mb-3"}`}
      onDragEnter={(e) => {
        // A file dragged over the bar opens the photo dialog to drop it in.
        if (!photoSearch || !Array.from(e.dataTransfer?.types ?? []).includes("Files")) return;
        setDropOpen(true);
      }}
      onDragOver={(e) => { if (photoSearch && Array.from(e.dataTransfer?.types ?? []).includes("Files")) e.preventDefault(); }}
      onDrop={onDropImage}
    >
      <ImageSearchDialog
        open={dropOpen}
        onClose={() => setDropOpen(false)}
        note={imageNote}
        onNote={setImageNote}
        onFile={(f) => void runImage(f)}
        onLink={(u) => { setImageNote(null); void runImageUrl(u); }}
      />
      {/* ── The bar: full width, and the applied filters LIVE in it as
             removable chips - what you entered is always visible and
             one tap from gone, no panel required. ── */}
      {/* Compact on a phone: one line that opens the sheet (the chips
          would wrap into a tall box beside the map's filter pill). */}
      {compact && (
        <button
          type="button"
          onClick={() => { captureEvent("magic_search_open", { surface: "sheet", applied: applied.length }); openSheet(); }}
          className="flex h-10 w-full items-center gap-2 rounded-xl border border-gray-200 bg-white/95 px-3 text-left text-sm text-gray-500 backdrop-blur-sm sm:hidden"
        >
          <Search className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2} aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">{applied.length ? applied.map((c) => c.label).join(", ") : t("listings.magic.placeholder_short")}</span>
          {applied.length > 0 && <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-gray-900 px-1 text-xs font-bold text-gray-50">{applied.length}</span>}
        </button>
      )}
      <div className={compact ? "hidden sm:block sm:absolute sm:inset-x-0 sm:top-0 sm:z-40" : "contents"}>
      <div
        onFocus={() => setFocusIn(true)}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusIn(false); }}
        onMouseDownCapture={(e) => {
          // Collapsed, the bar is a summary: a click anywhere on it (chips,
          // "+N", the empty space) opens it; only the clear cross acts
          // directly (owner, 2026-10-12: the chips and icons left no room
          // to click back in).
          if (!compact || expanded) return;
          if ((e.target as HTMLElement).closest("[data-bar-action]")) return;
          e.preventDefault();
          e.stopPropagation();
          inputRef.current?.focus();
          setOpen(true);
        }}
        className={`relative min-h-10 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 transition-colors focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 ${
          compact ? (expanded ? "flex flex-wrap shadow-[0_8px_30px_-12px_rgba(21,23,31,0.18)]" : "flex h-10 flex-nowrap overflow-hidden") : chipsOutside ? "flex flex-nowrap" : "flex flex-wrap"
        } ${showClear ? (advice.length > 0 && !open && !sheet ? "pr-[5.75rem]" : "pr-10") : advice.length > 0 && !open && !sheet ? "pr-16" : ""}`}>
        {aiEnabled && (
        <span
          className="inline-flex shrink-0 items-center gap-2 pl-1 text-[11px] font-extrabold tracking-[0.16em] text-gray-500"
          title={t("listings.magic.ai_title")}
        >
          AI
          <span className="h-4 w-px bg-gray-200" aria-hidden="true" />
        </span>
        )}
        {!chipsOutside && shownChips.map((c) => (
          <button
            key={c.id}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); captureEvent("magic_search_chip_removed", { chip: c.id.split(":")[0] }); removeChip(c); }}
            className={`group inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-100 py-0.5 pl-2.5 pr-1.5 text-xs font-medium text-gray-800 hover:border-gray-400 ${compact && !expanded ? "max-w-[9rem] pr-2.5" : ""}`}
            aria-label={`${c.title ? c.title + ": " : ""}${c.label} - ${t("listings.filter.reset")}`}
            title={c.title}
          >
            {c.icon === "text" && <TextSearch className="h-3.5 w-3.5 shrink-0 text-gray-500" strokeWidth={2} aria-hidden="true" />}
            <span className={compact && !expanded ? "truncate" : ""}>{c.label}</span>
            {(!compact || expanded) && <X className="h-3.5 w-3.5 shrink-0 rounded-full text-gray-400 group-hover:text-gray-900" strokeWidth={2} />}
          </button>
        ))}
        {!chipsOutside && hiddenChips > 0 && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); inputRef.current?.focus(); }}
            className="inline-flex shrink-0 items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700 hover:bg-gray-50"
            aria-label={`+${hiddenChips}`}
          >
            +{hiddenChips}
          </button>
        )}
        <input
          ref={inputRef}
          type="text"
          value={sheet ? "" : q}
          onChange={(e) => {
            setQ(e.target.value);
            setHint(null);
            navigated.current = false;
            if (dropOpen) setDropOpen(false);
            // Typing reopens the panel: after a pick/parse closes it,
            // focus never leaves the input, so onFocus can't refire.
            if (!sheet) setOpen(true);
          }}
          onKeyDown={onKey}
          onPaste={onPasteImage}
          // A pick closes the panel while the input keeps focus, so
          // onFocus cannot bring it back: a click on the focused input
          // reopens it (owner, 2026-09-20: "I need to click outside and
          // back on it to see the options").
          onClick={() => {
            if (!open && !window.matchMedia("(max-width: 639px)").matches) {
              captureEvent("magic_search_open", { surface: "bar", applied: applied.length });
              setOpen(true);
            }
          }}
          onFocus={(e) => {
            // Phones get the full-screen sheet, not a cramped dropdown.
            if (window.matchMedia("(max-width: 639px)").matches) {
              e.currentTarget.blur();
              captureEvent("magic_search_open", { surface: "sheet", applied: applied.length });
              openSheet();
              return;
            }
            if (!open) captureEvent("magic_search_open", { surface: "bar", applied: applied.length });
            setOpen(true);
          }}
          placeholder={compact && !expanded && applied.length ? "" : t(applied.length ? "listings.magic.placeholder_more" : aiEnabled ? "listings.magic.placeholder_short" : "listings.magic.placeholder_keyword")}
          // With the chips outside (the list page) nothing else needs a
          // second line: the field gives way and its placeholder ends in
          // an ellipsis. Its 140 px floor sent the camera to a second line
          // on 360 px phones once Filtrlər sat beside the bar (2026-11-01,
          // about a fifth of phone visits are under 375 px).
          className={`flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400 ${compact && !expanded ? (applied.length ? "min-w-[1rem]" : "min-w-[6rem]") : chipsOutside ? "min-w-0 text-ellipsis" : "min-w-[140px]"}`}
          role="combobox"
          aria-expanded={open}
          aria-label={t("listings.magic.placeholder_short")}
          autoComplete="off"
        />
        {/* A suggestion is waiting: an amber lightbulb that opens the
            panel on it (the bar is where suggestions live). Pinned to
            the bar's top-right corner, so it stays put when the chips
            wrap the bar taller (owner, 2026-10-11). */}
        {advice.length > 0 && !open && !sheet && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setQ(""); setOpen(true); }}
            aria-label={t("listings.magic.suggested")}
            title={t("listings.magic.suggested")}
            className={`absolute top-2 inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 ${showClear ? "right-10" : "right-2.5"}`}
          >
            <Lightbulb className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
            <span className="tabular-nums">{advice.length}</span>
          </button>
        )}
        {showClear && (
          <button
            type="button"
            data-bar-action
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              captureEvent("magic_search_clear", { applied: chipsOutside ? 0 : applied.length, typed: q.trim().length > 0 });
              setQ("");
              setOpen(false);
              if (!chipsOutside) onClearAll?.();
            }}
            aria-label={chipsOutside ? t("listings.magic.clear_text") : t("listings.filter.reset")}
            title={chipsOutside ? t("listings.magic.clear_text") : t("listings.filter.reset")}
            // Always the top-right corner, however many rows the chips take.
            // A 40 px target: the 28 px one could not be hit on phones
            // (UI critique, 2026-10-18).
            className="absolute right-0 top-0 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
          </button>
        )}
        {busy && !sheet && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" aria-hidden="true" />
        )}
        {/* Search by photo: the camera opens the picker; a photo pasted
            into the box or dropped on it runs the same search. */}
        {!sheet && photoSearch && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setImageNote(null);
              setDropOpen(true);
            }}
            aria-haspopup="dialog"
            aria-label={t("listings.magic.image.button")}
            title={t("listings.magic.image.hint")}
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 ${showClear ? "mr-7" : ""}`}
          >
            {imageBusy
              ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />
              : <Camera className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
          </button>
        )}
      </div>

      {/* ── Desktop panel: the mobile sheet, smaller and anchored. ── */}
      {open && !sheet && (
        <div className={`absolute z-30 mt-1.5 max-h-[26rem] w-full overflow-y-auto overflow-x-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-[0_8px_30px_-12px_rgba(21,23,31,0.18)] ${compact ? "min-w-[26rem]" : ""}`}>
          {panelBody(false)}
        </div>
      )}
      </div>

      {/* ── Mobile search sheet ── */}
      {sheet && mounted && createPortal(
        <div
          className={`search-sheet-in fixed inset-0 z-[1300] flex flex-col bg-white transition-[opacity,translate] duration-200 ease-out motion-reduce:transition-none ${
            sheetOn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <div className="flex items-center gap-1.5 border-b border-gray-100 px-2 py-2">
            <button
              type="button"
              onClick={closeSheet}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label={t("listings.location.close")}
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={2} />
            </button>
            <div className="flex h-10 flex-1 items-center gap-2.5 rounded-xl bg-gray-100 px-3.5">
              {aiEnabled && (
              <span
                className="inline-flex shrink-0 items-center gap-2 text-[11px] font-extrabold tracking-[0.16em] text-gray-500"
                title={t("listings.magic.ai_title")}
              >
                AI
                <span className="h-4 w-px bg-gray-300" aria-hidden="true" />
              </span>
              )}
              <input
                type="text"
                enterKeyHint="search"
                value={q}
                onChange={(e) => { setQ(e.target.value); setHint(null); navigated.current = false; }}
                onKeyDown={onKey}
                placeholder={t(aiEnabled ? "listings.magic.placeholder_short" : "listings.magic.placeholder_keyword")}
                autoFocus
                onPaste={onPasteImage}
                className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-gray-400"
                autoComplete="off"
              />
              {photoSearch && (
                <button
                  type="button"
                  onClick={() => { setImageNote(null); setDropOpen(true); }}
                  aria-haspopup="dialog"
                  aria-label={t("listings.magic.image.button")}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200"
                >
                  {imageBusy
                    ? <Loader2 className="h-[18px] w-[18px] animate-spin text-gray-400" aria-hidden="true" />
                    : <Camera className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" />}
                </button>
              )}
              {busy ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" aria-hidden="true" />
              ) : q ? (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200"
                  aria-label={t("listings.location.clear")}
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-1">
            {panelBody(true)}
          </div>

          <div className="border-t border-gray-100 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {/* A link on a home bar: the sheet's button values it. */}
            <button
              type="button"
              onClick={evaluating ? () => runEvaluate(trimmed) : closeSheet}
              className="w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white"
            >
              {t(evaluating ? "listings.magic.evaluate_cta" : "listings.magic.show_results")}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

type MagicSearchBarProps = Omit<Parameters<typeof MagicSearchBarBody>[0], "urlParams">;

function MagicSearchBarFromUrl(props: MagicSearchBarProps) {
  return <MagicSearchBarBody {...props} urlParams={useSearchParams()} />;
}

/** With an explicit `searchParams` (a landing page) the bar never reads
 *  the URL, so a statically rendered page keeps it in its server HTML. */
/** A photo may not travel at phone size. Anything past CLIENT_MAX_BYTES
 *  is refused outright; a larger-than-needed one is decoded and shrunk
 *  to UPLOAD_MAX_SIDE on the longest side as a JPEG (the model sees
 *  384 px; the backend keeps 12 MB as its own ceiling). A file the
 *  browser cannot decode (HEIC on Chrome) goes as it is when it fits. */
const CLIENT_MAX_BYTES = 20 * 1024 * 1024;
const SERVER_MAX_BYTES = 12 * 1024 * 1024;
const UPLOAD_MAX_SIDE = 1600;
const SHRINK_ABOVE_BYTES = 600 * 1024;

function tooLarge(): Error {
  const e = new Error("file_too_large");
  (e as Error & { code?: string }).code = "file_too_large";
  return e;
}

async function prepareUpload(file: Blob): Promise<Blob> {
  if (file.size > CLIENT_MAX_BYTES) throw tooLarge();
  if (file.size <= SHRINK_ABOVE_BYTES) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, UPLOAD_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= SERVER_MAX_BYTES) { bitmap.close(); return file; }
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
      if (out && out.size > 0) return out;
    } else {
      bitmap.close();
    }
  } catch {
    /* undecodable here (HEIC on Chrome): send it as it is below */
  }
  if (file.size > SERVER_MAX_BYTES) throw tooLarge();
  return file;
}

export default function MagicSearchBar(props: MagicSearchBarProps) {
  if (props.searchParams) return <MagicSearchBarBody {...props} urlParams={props.searchParams} />;
  return <MagicSearchBarFromUrl {...props} />;
}
